import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal, Signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import {
  ChurnFlowProductsBaseService,
  ChurnFlowReasonInfoboxService,
} from '@core/churn-flow-logic';
import {
  ChurnFlowSolutionCardI,
  ChurnFlowSupportedLocale,
  ChurnReason,
} from '@core/churn-flow-types';
import {
  ChurnFlowReasonTemplateComponent,
  ChurnFlowSolutionCardComponent,
  ChurnFlowStepTemplateComponent,
} from '@core/churn-flow-ui';
import { TranslatePipe } from '@core/i18n-legacy';
import { ButtonComponent, IconComponent } from '@core/user-interface-legacy';

import { ChurnFlowInsightsComponent } from '../../components/churn-flow-insights/churn-flow-insights.component';
import { ChurnFlowReasonBaseComponent } from '../../components/churn-flow-reason-base.component';

@Component({
  selector: 'tlc-churn-not-hiring-page',
  templateUrl: './churn-not-hiring-page.component.html',
  styleUrls: ['./churn-not-hiring-page.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ChurnFlowStepTemplateComponent,
    ChurnFlowInsightsComponent,
    TranslatePipe,
    ButtonComponent,
    ChurnFlowSolutionCardComponent,
    NgOptimizedImage,
    IconComponent,
    ChurnFlowReasonTemplateComponent,
  ],
})
export class ChurnNotHiringPageComponent extends ChurnFlowReasonBaseComponent {
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly productsService = inject(ChurnFlowProductsBaseService);

  protected readonly reason = ChurnReason.NotHiring;

  public readonly isTellentHrActive = this.productsService.isTellentHrActive;
  public readonly infoboxMessage: Signal<string | undefined> = inject(
    ChurnFlowReasonInfoboxService,
  ).getReasonInfoboxMessage(ChurnReason.NotHiring);

  public readonly tellentHrSolution: ChurnFlowSolutionCardI = {
    iconVariant: 'tellent-hr',
    title: this.t('tlc.churn_flow.steps.not_hiring.solutions.add_hris.title'),
    subtitle: this.t('tlc.churn_flow.steps.not_hiring.solutions.add_hris.subtitle'),
    incentives: [
      this.t('tlc.churn_flow.steps.not_hiring.solutions.add_hris.incentive_1'),
      this.t('tlc.churn_flow.steps.not_hiring.solutions.add_hris.incentive_2'),
    ],
    ctaText: this.isTellentHrActive()
      ? this.t('tlc.churn_flow.steps.not_hiring.solutions.add_hris.cta_alternate')
      : this.t('tlc.churn_flow.steps.not_hiring.solutions.add_hris.cta'),
    secondaryCtaText: this.t('tlc.churn_flow.steps.not_hiring.solutions.add_hris.cta_secondary'),
  };

  public readonly pauseSubscriptionSolution: ChurnFlowSolutionCardI = {
    iconVariant: 'pause-subscription',
    title: this.t('tlc.churn_flow.steps.not_hiring.solutions.pause_subscription.title'),
    subtitle: this.t('tlc.churn_flow.steps.not_hiring.solutions.pause_subscription.subtitle'),
    incentives: [
      this.t('tlc.churn_flow.steps.not_hiring.solutions.pause_subscription.incentive_1'),
      this.t('tlc.churn_flow.steps.not_hiring.solutions.pause_subscription.incentive_2'),
      this.t('tlc.churn_flow.steps.not_hiring.solutions.pause_subscription.incentive_3'),
    ],
    ctaText: this.t('tlc.churn_flow.steps.not_hiring.solutions.pause_subscription.cta'),
  };

  public displayYoutubeVideo = signal<boolean>(false);

  public activateHrFreeTrial(): void {
    this.churnFlowDataService.activateTellentHrFreeTrial();
  }

  public pauseSubscription(): void {
    this.openIntercom(
      this.t('tlc.churn_flow.steps.not_hiring.solutions.pause_subscription.intercom_message'),
    );
  }

  public loadYoutubeVideo(): void {
    this.displayYoutubeVideo.set(true);
  }

  public getVideoUrl(): SafeResourceUrl {
    let url: string;

    switch (this.locale) {
      case ChurnFlowSupportedLocale.Dutch:
        url = 'https://www.youtube.com/embed/NH6ojAi-upU?si=LvczM-LwPJCO-QQH';
        break;
      case ChurnFlowSupportedLocale.German:
        url = 'https://www.youtube.com/embed/r0PHJyQ6MoA?si=7EX9xrvjbCd7e4Jo';
        break;
      case ChurnFlowSupportedLocale.RestOfWorld:
        url = 'https://www.youtube.com/embed/-iV2ySj_58Y?si=dNNbt5xFQu4TzccQ';
        break;
      case ChurnFlowSupportedLocale.French:
        url = 'https://www.youtube.com/embed/EVlVev1SzSE?si=JqjoDFg9O32c6ipN';
        break;
      default:
        url = 'https://www.youtube.com/embed/-iV2ySj_58Y?si=dNNbt5xFQu4TzccQ';
        break;
    }

    // eslint-disable-next-line ban/ban
    return this.domSanitizer.bypassSecurityTrustResourceUrl(url);
  }
}
